import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";

// Icons
import { PencilIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import LoaderContext from "../../../context/psikotest/LoaderContext";
import SnackbarContext from "../../../context/psikotest/SnackbarContext";

// Helper
import errorHandler from "../../../helper/psikotest/errorHandler";
import logRender from "../../../helper/psikotest/logRender";

// Controller
import Ist from "../../../controller/psikotest/psikotest_system/ist";

// Component
import TableFilter from "../../../components/psikotest/psikotest_system/TableFilter";
import Table from "../../../components/psikotest/psikotest_system/Table";
import { SnackbarObject } from "../../../context/psikotest/Type";

function List() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  const [list, setList] = useState<Array<TPSIst>>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "ist/List" });
  }, []);

  const getList = useCallback(
    async (setNotif: Dispatch<SetStateAction<SnackbarObject>>) => {
      try {
        setMessage("Fetch Ist");
        const ic = new Ist();
        const res = await ic.list();
        setList(res.data.list);
        setMessage("");
      } catch (error) {
        setMessage("");
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [setMessage]
  );

  useEffect(() => {
    getList(setNotif);
  }, [getList, setNotif]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5 pb-20">
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <IstList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function IstList(props: { list: Array<TPSIst>; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (ist: TPSIst) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "All question with this ist will also be removed, You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const ic = new Ist();
          await ic.delete(ist);
          props.handleDeleteList(ist.id);
          setNotif({ type: "success", message: "ist deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Subtest Name",
        accessor: "id",
        show: isMobile,
        Cell: (props: { row: { original: TPSIst } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.subtest_name || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "subtest_name",
        classHeader: "text-left px-2",
        Header: "Subtest Name",
        classDivHeader: "flex items-center",
        accessor: "subtest_name",
        show: !isMobile,
        Cell: (props: { row: { original: TPSIst } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.subtest_name || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "mdi",
        classHeader: "text-left px-2",
        Header: "MDI",
        classDivHeader: "flex items-center",
        accessor: "mdi",
        show: !isMobile,
        Cell: (props: { row: { original: TPSIst } }) => {
          return props.row.original.mdi || "NULL";
        },
      },
      {
        id: "hide_mdi_within",
        classHeader: "text-left px-2",
        Header: "Show MDI (Time)",
        classDivHeader: "flex items-center",
        accessor: "hide_mdi_within",
        show: !isMobile,
        Cell: (props: { row: { original: TPSIst } }) => {
          return props.row.original.hide_mdi_within || "-";
        },
      },
      {
        id: "time_limit",
        classHeader: "text-left px-2",
        Header: <div title="Time Limit (Minutes)">Time Limit (Minutes)</div>,
        classDivHeader: "flex items-center",
        accessor: "time_limit",
        show: !isMobile,
        Cell: (props: { row: { original: TPSCfit } }) => {
          return props.row.original.time_limit
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TPSIst } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}

export default List;
